import { CCol, CCollapse, CForm, CFormInput, CFormLabel, CFormSwitch, CFormTextarea, CLoadingButton, CPlaceholder } from '@coreui/react-pro'
import { z } from 'zod';
import { useForm, SubmitHandler, Controller } from "react-hook-form"
import { zodResolver } from '@hookform/resolvers/zod';
import api from '../../utils/api'
import { useNavigate } from 'react-router-dom'
import useToast from '../../hooks/toast'
import {useContext, useEffect, useState} from 'react';
import useModalDelete from '../../hooks/modalDelete';
import { cisChevronBottom, cisChevronTop } from '@coreui/icons-pro';
import CIcon from '@coreui/icons-react';
import { IOption } from '../../types/inputs/select';
import RiSelect from "../../components/inputs/Select";
import {GenericErrorHandler} from "../../utils/misc";
import {AppContext} from "../../contexts/App";
import { BEBANS } from '../../constants/endpoint';

export function FormAddEdit({ edit, title }: { edit?: string, title: string }) {
    const {theme}= useContext(AppContext)
    const [collapseVisible, setCollapseVisible] = useState(false)
    const [addToast] = useToast()
    const navigate = useNavigate();
    const [isRetrievingData, setIsRetrievingData] = useState(false)
    const FormSchema = z.object({
        beban: z.string().nullable(),
        keterangan: z.string().nullable(),
        temp_akun_debit_id: z.number().nullable(),
        temp_akun_kredit_id: z.number().nullable(),
        is_active: z.boolean().optional(),
    });

    type FormSchemaType = z.infer<typeof FormSchema>;
    const initial = {
        is_active: true,
        beban: null,
        keterangan: null,
        temp_akun_debit_id: null,
        temp_akun_kredit_id: null,
    };
    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting },
        setError,
        control,
        reset,
        watch
    } = useForm<FormSchemaType>({
        resolver: zodResolver(FormSchema),
        defaultValues: (edit !== undefined
            ? initial
            : { ...JSON.parse(localStorage.getItem(title) || JSON.stringify(initial)) }
        )
    })

    const instance = watch()

    useEffect(() => {
        if (edit === undefined) {
            localStorage.setItem(title, JSON.stringify(instance))
        }
    }, [edit, instance, title])

    useEffect(() => {
        if (edit !== undefined) {
            setIsRetrievingData(true)
            api.get(`${BEBANS}/${edit}`).then(onfulfilled => {
                if (onfulfilled.data) {
                    if ( onfulfilled.data.is_active === false) {
						setCollapseVisible(true)
					}
                    reset(onfulfilled.data)
                }
                setIsRetrievingData(false)
            }).catch(reason => {
                GenericErrorHandler({reason, addToast})
            })
        } else {
            setIsRetrievingData(false)
        }
    }, [addToast, edit, reset])

    const additionalErrorHandler = (reason : any) => {
        for (let item in reason.response.data.errors) {
            setError(item as ("beban" | "keterangan" | "temp_akun_debit_id" | "temp_akun_kredit_id" | "is_active"), { type: 'custom', message: reason.response.data.errors[item][0] })
        }
    }

    const onCreate: SubmitHandler<FormSchemaType> = async (data) => {
        await api.post(BEBANS, data).then(() => {
            addToast({ message: `${title} Successed`, color: "success" })
            navigate(-1)
            reset(initial)
        }).catch(reason => {
            GenericErrorHandler({reason, addToast, additionalErrorHandler})
        });
    }

    const onUpdate: SubmitHandler<FormSchemaType> = async (data) => {
        await api.put(`${BEBANS}/${edit}`, data).then(() => {
            addToast({ message: `${title} Successed`, color: "success" })
            navigate(-1)
        }).catch(reason => {
            GenericErrorHandler({reason, addToast, additionalErrorHandler})
        });
    }

    const [UiModalDelete, UiButtonDelete] = useModalDelete({
        title: `Delete Beban #${edit}`,
        path: `/bebans/${edit}`,
        confirmation: <>Saya yakin ingin menghapus <strong>Beban #{edit}</strong></>
    })

    if (isRetrievingData) return (<CPlaceholder xs={12} size="lg" />)

    return (<>
        <CForm
            id='formSubmit'
            className="row"
            onSubmit={handleSubmit(edit !== undefined ? onUpdate : onCreate)}
            autoComplete='false'
        >
            <CFormLabel htmlFor="beban" className="col-md-2 col-form-label">Beban</CFormLabel>
            <CCol md={10} className='mb-3'>
                <CFormInput
                    placeholder='Beban'
                    invalid={errors.beban != null}
                    feedbackInvalid={errors.beban?.message as string}
                    {...register('beban')}
                />
            </CCol>
            <CFormLabel htmlFor="keterangan" className="col-md-2 col-form-label">Keterangan</CFormLabel>
            <CCol md={10} className='mb-3'>
                <CFormTextarea
                    placeholder='Keterangan'
                    invalid={errors.keterangan != null}
                    feedbackInvalid={errors.keterangan?.message as string}
                    {...register('keterangan')}
                />
            </CCol>
            <CFormLabel htmlFor="temp_akun_kredit_id" className="col-md-2 col-form-label">Akun Kredit</CFormLabel>
                    <CCol md={10} className='mb-3' >
                        <Controller
                            name="temp_akun_kredit_id"
                            control={control}
                            render={({ field: { onChange, value }, fieldState: { error } }) => (
                                <RiSelect
                                    type={"akun"}
                                    isSub={'false'}
                                    placeholder='Select Akun Kredit'
                                    feedbackInvalid={error?.message}
                                    invalid={error != null}
                                    value={value || undefined}
                                    onChange={(newValue) => {
                                        if (newValue) {
                                            const data = newValue as IOption
                                            onChange(data.value as number)
                                        } else {
                                            onChange(undefined)
                                        }
                                    }}
                                    theme={theme}
                                />
                            )}
                        />
                    </CCol>
                    <CFormLabel htmlFor="temp_akun_debit_id" className="col-md-2 col-form-label">Akun Debit</CFormLabel>
                    <CCol md={10} className='mb-3'>
                        <Controller
                            name="temp_akun_debit_id"
                            control={control}
                            render={({ field: { onChange, value }, fieldState: { error } }) => (
                                <RiSelect
                                    type={"akun"}
                                    isSub={'false'}
                                    placeholder='Select Akun Debit'
                                    feedbackInvalid={error?.message}
                                    invalid={error != null}
                                    value={value || undefined}
                                    onChange={(newValue) => {
                                        if (newValue) {
                                            const data = newValue as IOption
                                            onChange(data.value as number)
                                        } else {
                                            onChange(undefined)
                                        }
                                    }}
                                    theme={theme}
                                />
                            )}
                        />
                    </CCol>
            <CCol xs={12} className='text-center mb-3'>
                <span style={{ cursor: 'pointer' }} onClick={() => {
                    setCollapseVisible(x => !x)
                }}>
                    <CIcon icon={collapseVisible ? cisChevronBottom : cisChevronTop} />
                </span>
            </CCol>
            <CCol>
                <CCollapse className='row' visible={collapseVisible}>
                    <CCol md={10} className='offset-md-2 mb-3' >
                        <CFormSwitch
                            label="Aktif"
                            invalid={errors.is_active != null}
                            {...register('is_active')}
                        />
                    </CCol>
                </CCollapse>
            </CCol>

            <CCol md={10} className='mb-3 offset-md-2'>
                <CLoadingButton form='formSubmit' loading={isSubmitting} type="submit" className='me-2'>{edit ? 'Update' : 'Create'}</CLoadingButton>
                <CLoadingButton color='secondary' className='me-2' onClick={() => {
                    reset(initial)
                }}>Clear</CLoadingButton>
                {edit && <UiButtonDelete />}
            </CCol>
        </CForm>
        {edit && <UiModalDelete />}
    </>)
}
